"use client";

import { useTranslations } from "next-intl";
import type { UseFormReturn } from "react-hook-form";
import { FormButtonSelect } from "@/components/form/form-button-select";
import type { MultidietPickerFormType } from "@/features/multidiet-picker/types/multidiet-picker-form.type";
import type { MultidietSetupSetupType } from "@/features/multidiet-picker/types/multidiet-setup.type";
import { cn } from "@/lib/utils";

export const MultidietPickerForm = ({
  layout,
  form,
  multidietSetup,
}: {
  layout: "page" | "popup";
  form: UseFormReturn<MultidietPickerFormType>;
  multidietSetup: MultidietSetupSetupType | null;
}) => {
  const t = useTranslations();

  return (
    <div
      className={cn("flex w-full flex-col items-center justify-center gap-4")}
    >
      <div
        className={cn(
          "flex gap-2 rounded-xl",
          layout === "popup" && "bg-brand-grey1",
        )}
      >
        {multidietSetup && (
          <FormButtonSelect
            fieldName="dietType"
            actionAfterChange={() => {
              const selected = form.getValues("dietType");
              if (
                Array.isArray(selected) &&
                selected.length > 0 &&
                form.getValues("dietTypeAny") !== "CUSTOM_CHOICE"
              ) {
                form.setValue("dietTypeAny", "CUSTOM_CHOICE");
              }
            }}
            className={cn(
              "flex-row",
              layout === "popup" && "rounded-xl bg-brand-grey1 p-2 pr-0",
            )}
            classNameItem="w-auto"
            classNameFormItem="w-auto"
            multi={true}
            form={form}
            fieldOptions={[
              {
                label: <span>{t(`dietTags.CUSTOM_CHOICE`)}</span>,
                value: "CUSTOM_CHOICE",
              },
            ]}
          />
        )}
        {multidietSetup && (
          <FormButtonSelect
            fieldName="dietTypeAny"
            actionAfterChange={() => {
              // switching to ANY clears explicit diet selections
              form.setValue("dietType", []);
            }}
            className={cn("flex-row", layout === "popup" && "py-2")}
            classNameItem="w-auto"
            classNameFormItem="w-auto "
            multi={false}
            form={form}
            fieldOptions={[
              { label: <span>{t(`dietTags.ANY`)}</span>, value: "ANY" },
            ]}
          />
        )}
        <div className="my-auto h-8 w-[1px] bg-brand-grey3" />
        {multidietSetup && (
          <FormButtonSelect
            fieldName="dietType"
            actionAfterChange={() => {
              const selected = form.getValues("dietType");
              if (
                Array.isArray(selected) &&
                selected.length > 0 &&
                form.getValues("dietTypeAny") !== "CUSTOM_CHOICE"
              ) {
                form.setValue("dietTypeAny", "CUSTOM_CHOICE");
              }
            }}
            className={cn(
              "flex-row flex-nowrap",
              layout === "popup" && "rounded-xl bg-brand-grey1 p-2 pl-0",
            )}
            classNameItem="w-auto"
            classNameFormItem="w-auto"
            multi={true}
            form={form}
            fieldOptions={multidietSetup.dietTypes
              .filter(
                (tag) =>
                  tag.dietTag !== "ANY" && tag.dietTag !== "CUSTOM_CHOICE",
              )
              .map((tag) => ({
                label: <span>{t(`dietTags.${tag.dietTag}`)}</span>,
                value: tag.dietTag,
              }))}
          />
        )}
      </div>
      {multidietSetup && (
        <FormButtonSelect
          fieldName="calories"
          className={cn(
            "flex-row",
            layout === "popup" && "rounded-xl bg-brand-grey1 p-2",
          )}
          classNameItem="w-auto"
          classNameFormItem="w-auto"
          multi={false}
          form={form}
          fieldOptions={multidietSetup.calories.map((calory) => ({
            label: <span>{t("common.kcal", { kcal: calory })}</span>,
            value: calory.toString(),
          }))}
        />
      )}
    </div>
  );
};
